import React, { useEffect, useCallback } from 'react';
import { client as BrainTreeClient, dataCollector } from 'braintree-web';
import { UseRecurlyInstance } from '@recurly/react-recurly';
import { datadogLogs } from '@datadog/browser-logs';
import { getEnv } from '../../utils/environment';

const VENMO_SUCCESS = 'Venmo payment success';
const VENMO_ERROR = 'Venmo error: ';
const VENMO_FLOW_CANCELED = 'Venmo flow canceled';
const BRAINTREE_ERROR = 'braintree datacollector failed to initialize';

export type RecurlyWithVenmo = {
  recurly: UseRecurlyInstance & { Venmo: any };
  checkoutTotal?: number;
  onVenmoVerify: (token: string) => void;
  onVenmoError: (error: string) => void;
  onVenmoCancel: () => void;
};

const Venmo: React.FunctionComponent<RecurlyWithVenmo> = ({
  recurly,
  checkoutTotal,
  onVenmoVerify,
  onVenmoError,
  onVenmoCancel,
}) => {
  const handleSubmit = (e?: React.SyntheticEvent) => e?.preventDefault();
  const [venmo, setVenmo] = React.useState<any>();

  const venmoFormRef = useCallback((nodeRef) => {
    if (nodeRef) {
      const venmoConfig = {
        display: {
          amount: checkoutTotal,
          currency: 'USD',
          displayName: 'FabFitFun',
          locale: 'en_US',
        },
        form: nodeRef,
        braintree: { clientAuthorization: getEnv().brainTreePublicKey },
      };
      const venmoInstance = recurly.Venmo(venmoConfig);
      setVenmo(venmoInstance);
      (window as any).venmo = venmoInstance;
    }
  }, []);

  useEffect(() => {
    if (!venmo) return;
    venmo.on('token', (token: any) => {
      datadogLogs.logger.info(VENMO_SUCCESS);
      onVenmoVerify(token.id);
    });

    venmo.on('error', (err: any) => {
      datadogLogs.logger.error(VENMO_ERROR, {}, err);
      onVenmoError(err.message);
    });

    venmo.on('cancel', () => {
      datadogLogs.logger.info(VENMO_FLOW_CANCELED);
      onVenmoCancel();
    });
  }, [venmo]);

  useEffect(() => {
    // Initialize BrainTree client
    BrainTreeClient.create({
      authorization: getEnv().brainTreePublicKey,
    }).then((clientInstance) => {
      // Get device data
      dataCollector.create(
        {
          client: clientInstance,
        },
        (dataCollectorErr, dataCollectorInstance) => {
          if (dataCollectorErr) {
            // Handle error in creation of data collector.
            const errorMessage = `errorCode: ${dataCollectorErr?.code} - message: ${dataCollectorErr?.message}`;
            const braintreeError = new Error(errorMessage);
            datadogLogs.logger.error(BRAINTREE_ERROR, {}, braintreeError);
            return;
          }
          // At this point, you should access the dataCollectorInstance.deviceData value and provide it
          // to your server, e.g. by injecting it into your form as a hidden input.
          const deviceData = dataCollectorInstance?.deviceData;

          // Provide data to recurly instance
          recurly.configure({
            publicKey: getEnv().recurlyPublicKey,
            fraud: {
              braintree: { deviceData },
            },
          });
        }
      );
    });
  }, []);

  return (
    <form onSubmit={handleSubmit} ref={venmoFormRef} data-testid="venmo-form">
      <input
        type="text"
        data-recurly="first_name"
        value="Venmo"
        hidden
        data-testid="firstname_label"
      />
      <input type="text" data-recurly="last_name" value="App" hidden data-testid="lastname_label" />
    </form>
  );
};

export default Venmo;
